import type { UseFetchOptions, UseFetchReturn } from '@vueuse/core';
import { useFetch } from '@vueuse/core';
import { isRef, type MaybeRefOrGetter, reactive, type UnwrapRef } from 'vue';
import type { Problem } from '@/api/sirta/problems';
import { type AnyObject, type APIResponse, ErrorCode } from '@/types';
import appGlobalStore from '@/store/appGlobal.store';

export interface UseAppFetchReturn<TData, TErr extends Problem = never> extends UseFetchReturn<TData> {
  apiResponse: UnwrapRef<APIResponse<TData, TErr>>;
}

export function useAppFetch<TData, TErr extends Problem = never>(
  url: MaybeRefOrGetter<string>,
): UseAppFetchReturn<TData, TErr>;
export function useAppFetch<TData, TErr extends Problem = never>(
  url: MaybeRefOrGetter<string>,
  fetchOptions: UseFetchOptions,
): UseAppFetchReturn<TData, TErr>;
export function useAppFetch<TData, TErr extends Problem = never>(
  url: MaybeRefOrGetter<string>,
  fetchOptions: UseFetchOptions,
  requestOptions: RequestInit,
): UseAppFetchReturn<TData, TErr>;
export function useAppFetch<TData, TErr extends Problem = never>(
  url: MaybeRefOrGetter<string>,
  fetchOptions: UseFetchOptions = {},
  requestOptions: RequestInit = {},
): UseAppFetchReturn<TData, TErr> {
  const apiResponse = reactive<APIResponse<TData, TErr>>({
    data: null,
    problem: undefined,
  });
  const { setBackendErrorThrown } = appGlobalStore();

  const { data, onFetchResponse, onFetchError, response, statusCode, ...otherFetchProps } = useFetch<TData>(
    url,
    requestOptions,
    {
      updateDataOnError: true,
      ...fetchOptions,

      async beforeFetch() {
        Object.assign(apiResponse, {
          data: null,
          problem: undefined,
        });
      },
    },
  );

  function isKeyOfErrorCode(httpStringCode: unknown): httpStringCode is keyof typeof ErrorCode {
    return typeof httpStringCode === 'string' && httpStringCode in ErrorCode;
  }

  onFetchError((err: AnyObject & Error) => {
    if (err.name === 'AbortError') {
      Object.assign(
        apiResponse,
        {
          data: null,
          problem: undefined,
        },
      );
      return;
    }

    // Met à jour l'indice indiquant qu'il y a eu un problème avec la requête backend
    setBackendErrorThrown(true);

    const httpStringCode = `HTTP_${statusCode.value}`;
    const isDefinedCode = isKeyOfErrorCode(httpStringCode);

    if (!isDefinedCode) {
      // Affiche dans la console toutes les erreurs Http qui ne sont pas gérées par l'application.
      // Cela inclut les requêtes avortées, qui ont été exécutées en double.
      window.console.error('Fetch :: Erreur inconnue retournée par l\'API', { url: isRef(url) ? url.value : url, err, statusCode: statusCode.value });
    }
    Object.assign(apiResponse, {
      data: null,
      problem: data.value || { reason: isDefinedCode ? ErrorCode[httpStringCode] : ErrorCode.HTTP_GENERIC },
    });
  });

  onFetchResponse(() => {
    // Assure un retour à la normale pour tous les fureteurs qui ne rapporte pas bien l'état online/offline
    setBackendErrorThrown(false);
    Object.assign(apiResponse, {
      data: data.value,
      problem: undefined,
    });
  });

  return {
    apiResponse,
    response,
    data,
    onFetchResponse,
    onFetchError,
    statusCode,
    ...otherFetchProps,
  };
}

export default useAppFetch;
